<template>
  <div>
  <transition name="splash-fade">
    <div v-if="showSplash" class="splash-overlay">
      <!-- PHASE 1: Mascot burst filling the screen -->
      <div v-if="burstPhase" class="burst-container">
        <div
          v-for="n in 30"
          :key="'burst-'+n"
          class="burst-img"
          :style="getBurstStyle(n)"
        >
          <img :src="burstImages[n % 3]" alt="" />
        </div>
      </div>

      <!-- Floating bubbles background (visible after burst) -->
      <div v-if="!burstPhase" class="bubbles">
        <div v-for="n in 20" :key="'b-'+n" class="bubble" :style="getBubbleStyle(n)"></div>
      </div>

      <!-- Sparkle stars (visible after burst) -->
      <div v-if="!burstPhase" class="sparkles">
        <div v-for="n in 12" :key="'s-'+n" class="sparkle" :style="getSparkleStyle(n)">
          <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
            <path d="M12 2l2.09 6.26L20.18 9l-5.09 3.74L17.18 19 12 15.27 6.82 19l2.09-6.26L3.82 9l6.09-.74z"/>
          </svg>
        </div>
      </div>

      <!-- Center content (visible after burst) -->
      <div v-if="!burstPhase" class="splash-center" :class="{ 'splash-center--visible': contentVisible }">
        <!-- Mascot with speech bubble -->
        <div class="splash-mascot-area">
          <div class="speech-bubble">
            <span class="speech-bubble__emoji">&#127881;</span>
            Hey there! We got a fresh new look!
            <span class="speech-bubble__emoji">&#10024;</span>
          </div>
          <div class="splash-mascot">
            <img src="/images/tax-buddy.png" alt="Tax Buddy" class="splash-mascot__img" />
          </div>
        </div>

        <h1 class="splash-headline">
          <span v-if="revealStep >= 1" class="splash-headline__line1">Something</span>
          <span v-if="revealStep >= 2" class="splash-headline__big">AWESOME</span>
          <span v-if="revealStep >= 3" class="splash-headline__line3">Just Happened!</span>
        </h1>

        <p v-if="revealStep >= 4" class="splash-sub">The HRDC system has been completely redesigned</p>

        <button v-if="revealStep >= 4" class="splash-cta" @click.stop="openModal">
          <span class="splash-cta__icon">&#128640;</span>
          Explore What's New
        </button>
      </div>

    </div>
  </transition>

  <!-- What's New Modal -->
  <transition name="modal-pop">
    <div v-if="showModal" class="wn-overlay" @click.self="closeAll">
      <div class="wn-card">
        <!-- Header with mascot -->
        <div class="wn-card__header">
          <div class="wn-card__header-shapes">
            <div class="wn-shape wn-shape--1"></div>
            <div class="wn-shape wn-shape--2"></div>
            <div class="wn-shape wn-shape--3"></div>
          </div>
          <div class="wn-card__header-content">
            <img src="/images/tax-buddy.png" alt="Tax Buddy" class="wn-card__mascot" />
            <div>
              <div class="wn-card__badge">&#127881; NEW UPDATE</div>
              <h2 class="wn-card__title">What's New in HRDC</h2>
            </div>
          </div>
          <button class="wn-card__close" @click="closeAll">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="18" height="18">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Features list -->
        <div class="wn-card__body">
          <div
            class="wn-item"
            v-for="(feature, index) in features"
            :key="index"
            :style="{ animationDelay: (index * 0.08) + 's' }"
          >
            <div class="wn-item__emoji">{{ feature.emoji }}</div>
            <div class="wn-item__info">
              <h4 class="wn-item__title">{{ feature.title }}</h4>
              <p class="wn-item__desc">{{ feature.desc }}</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="wn-card__footer">
          <button class="wn-card__btn" @click="closeAll">
            <span>&#128079;</span> Awesome, Let's Go!
          </button>
        </div>
      </div>
    </div>
  </transition>
  </div>
</template>

<script>
const SPLASH_KEY = "hrdc_ui_splash_v2_seen";

export default {
  name: "WelcomeSplash",
  data: () => ({
    showSplash: false,
    showModal: false,
    contentVisible: false,
    revealStep: 0,
    burstPhase: true,
    burstImages: [
      '/images/tax-buddy.png',
      '/images/tax-buddy-2.png',
      '/images/tax-buddy-3.png',
    ],
    features: [
      {
        emoji: "\u{1F3A8}",
        title: "Fresh New Look",
        desc: "Modern cards, clean layouts, and beautiful gradients everywhere.",
      },
      {
        emoji: "\u{1F4CA}",
        title: "Better Data Tables",
        desc: "Color-coded badges, pill tags, and smoother interactions.",
      },
      {
        emoji: "\u{2709}\u{FE0F}",
        title: "Email Compose Preview",
        desc: "Compose emails in a real email client-like experience.",
      },
      {
        emoji: "\u{1F4F1}",
        title: "Mobile Friendly",
        desc: "Everything works great on phones and tablets too!",
      },
{
        emoji: "\u{1F680}",
        title: "Faster Workflows",
        desc: "Toggle cards, file cards, and smart interactions save you time.",
      },
    ],
  }),
  mounted() {
    // Master switch from .env — if "false", never show splash regardless of localStorage
    const splashEnabled = process.env.VUE_APP_SHOW_SPLASH !== "false";
    if (splashEnabled && !localStorage.getItem(SPLASH_KEY)) {
      this.showSplash = true;
      this.burstPhase = true;

      // After pile-up animation completes, transition to main content
      setTimeout(() => {
        this.burstPhase = false;
        setTimeout(() => { this.contentVisible = true; }, 100);
      }, 4500);

      // Dramatic sequence reveal (after pile-up)
      setTimeout(() => { this.revealStep = 1; }, 5500);   // "Something"
      setTimeout(() => { this.revealStep = 2; }, 6500);   // "AWESOME"
      setTimeout(() => { this.revealStep = 3; }, 7700);   // "Just Happened!"
      setTimeout(() => { this.revealStep = 4; }, 8700);   // subtitle + button
    }
  },
  methods: {
    getBurstStyle(n) {
      // Volcano eruption — shoot up from bottom center then scatter
      const spread = -50 + Math.random() * 100;  // horizontal scatter
      const height = 40 + Math.random() * 60;     // how high it flies
      const size = 70 + (n % 5) * 10 + Math.random() * 40;
      const rotation = -40 + Math.random() * 80;
      const delay = Math.random() * 0.3;            // almost all at once
      const duration = 3 + Math.random() * 1;     // smooth 3-4s arc

      return {
        '--volc-spread': spread + 'vw',
        '--volc-height': height + 'vh',
        '--volc-rot': rotation + 'deg',
        '--volc-size': size + 'px',
        animationDelay: delay + 's',
        animationDuration: duration + 's',
      };
    },
    getBubbleStyle(n) {
      const colors = [
        "rgba(14,165,233,0.15)", "rgba(139,92,246,0.12)", "rgba(236,72,153,0.1)",
        "rgba(245,158,11,0.12)", "rgba(16,185,129,0.12)", "rgba(99,102,241,0.1)",
      ];
      const size = 40 + Math.random() * 120;
      return {
        width: size + "px",
        height: size + "px",
        left: (Math.random() * 100) + "%",
        top: (Math.random() * 100) + "%",
        background: colors[n % colors.length],
        animationDelay: (Math.random() * 4) + "s",
        animationDuration: (6 + Math.random() * 6) + "s",
      };
    },
    getSparkleStyle(n) {
      const colors = ["#fbbf24", "#f59e0b", "#fcd34d", "#fff", "#a78bfa", "#ec4899"];
      const size = 12 + Math.random() * 20;
      return {
        width: size + "px",
        height: size + "px",
        left: (Math.random() * 100) + "%",
        top: (Math.random() * 100) + "%",
        color: colors[n % colors.length],
        animationDelay: (Math.random() * 3) + "s",
        animationDuration: (1.5 + Math.random() * 2) + "s",
      };
    },
    openModal() {
      this.showSplash = false;
      this.showModal = true;
      localStorage.setItem(SPLASH_KEY, "true");
    },
    closeAll() {
      this.showModal = false;
      localStorage.setItem(SPLASH_KEY, "true");
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .speech-bubble {
  background: var(--app-card);
  color: var(--app-text);
}

.dark-mode .speech-bubble::after {
  border-right: 10px solid var(--app-card);
}

.dark-mode .splash-cta {
  color: var(--app-text);
}

.dark-mode .wn-card {
  background: var(--app-card);
}

.dark-mode .wn-card__badge {
  color: var(--app-text);
}

.dark-mode .wn-item {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .wn-item__emoji {
  background: var(--app-surface);
}

.dark-mode .wn-item__title {
  color: var(--app-text);
}

.dark-mode .wn-item__desc {
  color: var(--app-text-muted);
}

.dark-mode .wn-card__btn {
  color: var(--app-text);
}

/* >>> end auto-dark <<< */

/* ===== SPLASH OVERLAY ===== */
.splash-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: linear-gradient(160deg, #1a1046, #0c2461, #0a3d62);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

/* ===== VOLCANO BURST ===== */
.burst-container {
  position: absolute;
  inset: 0;
  z-index: 5;
  overflow: hidden;
}

.burst-img {
  position: absolute;
  left: 50%;
  bottom: -10%;
  opacity: 0;
  animation: volcano-erupt ease-out forwards;
}

.burst-img img {
  width: var(--volc-size);
  height: auto;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.3));
}

@keyframes volcano-erupt {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) rotate(0deg) scale(0.2);
  }
  5% {
    opacity: 1;
  }
  40% {
    opacity: 1;
    transform:
      translate(
        calc(-50% + var(--volc-spread)),
        calc(-1 * var(--volc-height))
      )
      rotate(var(--volc-rot))
      scale(1);
  }
  100% {
    opacity: 0;
    transform:
      translate(
        calc(-50% + var(--volc-spread) * 1.1),
        30vh
      )
      rotate(calc(var(--volc-rot) * 1.3))
      scale(0.3);
  }
}

/* ===== FLOATING BUBBLES ===== */
.bubbles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  animation: bubble-float ease-in-out infinite alternate;
}

@keyframes bubble-float {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(30px, -40px) scale(1.15); }
}

/* ===== SPARKLE STARS ===== */
.sparkles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.sparkle {
  position: absolute;
  animation: sparkle-pulse ease-in-out infinite;
}

@keyframes sparkle-pulse {
  0%, 100% { opacity: 0.2; transform: scale(0.6) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
}

/* ===== CENTER CONTENT ===== */
.splash-center {
  text-align: center;
  z-index: 1;
  opacity: 0;
  transform: translateY(40px) scale(0.9);
  transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.splash-center--visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* ===== MASCOT AREA ===== */
.splash-mascot-area {
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
}

.splash-mascot {
  animation: mascot-bounce 2s ease-in-out infinite;
}

.splash-mascot__img {
  width: 180px;
  height: auto;
  filter: drop-shadow(0 12px 30px rgba(0, 0, 0, 0.3));
}

@keyframes mascot-bounce {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-18px) rotate(2deg); }
}

/* ===== SPEECH BUBBLE ===== */
.speech-bubble {
  position: absolute;
  top: -10px;
  right: -240px;
  background: #fff;
  color: #1e293b;
  padding: 10px 18px;
  border-radius: 18px;
  font-family: 'Baloo 2', cursive;
  font-size: 0.88rem;
  font-weight: 700;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: speech-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s both;
  transform: rotate(-4deg);
  white-space: nowrap;
  z-index: 2;
}

.speech-bubble::after {
  content: '';
  position: absolute;
  bottom: 10px;
  left: -8px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 10px solid #fff;
}

.speech-bubble__emoji {
  font-size: 1rem;
}

@keyframes speech-pop {
  from { opacity: 0; transform: rotate(-4deg) scale(0.3); }
  to { opacity: 1; transform: rotate(-4deg) scale(1); }
}

/* ===== HEADLINE ===== */
.splash-headline {
  margin: 0 0 16px;
  line-height: 1.1;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.splash-headline__line1 {
  display: block;
  font-family: 'Fredoka One', cursive;
  font-size: 3.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  animation: slide-in-down 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.splash-headline__big {
  display: block;
  font-family: 'Fredoka One', cursive;
  font-size: 6rem;
  font-weight: 400;
  background: linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6, #fbbf24);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 2px;
  animation: slam-in 0.5s cubic-bezier(0.22, 1.5, 0.36, 1) both;
}

.splash-headline__line3 {
  display: block;
  font-family: 'Fredoka One', cursive;
  font-size: 3.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  animation: slide-in-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes slide-in-down {
  from { opacity: 0; transform: translateY(-40px) scale(0.8); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes slam-in {
  0% { opacity: 0; transform: scale(3); }
  60% { opacity: 1; transform: scale(0.9); }
  80% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes slide-in-up {
  from { opacity: 0; transform: translateY(40px) scale(0.8); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes text-up {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.splash-sub {
  font-family: 'Baloo 2', cursive;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 28px;
  animation: text-up 0.5s ease both;
}

/* ===== CTA BUTTON ===== */
.splash-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 36px;
  border-radius: 50px;
  border: none;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #1e293b;
  font-family: 'Baloo 2', cursive;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(251, 191, 36, 0.3);
  animation: text-up 0.5s ease both;
}

.splash-cta:hover {
  transform: translateY(-3px) scale(1.03);
  box-shadow: 0 8px 30px rgba(251, 191, 36, 0.5);
}

.splash-cta__icon {
  font-size: 1.2rem;
}

/* ===== SKIP TEXT ===== */
.splash-skip-text {
  position: absolute;
  bottom: 24px;
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.25);
  z-index: 1;
}

/* ===== SPLASH TRANSITION ===== */
.splash-fade-leave-active {
  transition: opacity 0.4s ease;
}
.splash-fade-leave-to {
  opacity: 0;
}

/* ===== WHAT'S NEW OVERLAY ===== */
.wn-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(10, 20, 40, 0.65);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* ===== WHAT'S NEW CARD ===== */
.wn-card {
  background: #fff;
  border-radius: 24px;
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  animation: card-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes card-pop {
  from { opacity: 0; transform: translateY(40px) scale(0.9); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.wn-card__header {
  position: relative;
  padding: 28px 24px 20px;
  background: linear-gradient(135deg, #1a1046, #0c2461);
  overflow: hidden;
}

.wn-card__header-shapes {
  position: absolute;
  inset: 0;
}

.wn-shape {
  position: absolute;
  border-radius: 50%;
}

.wn-shape--1 {
  width: 120px;
  height: 120px;
  top: -40px;
  right: -20px;
  background: rgba(139, 92, 246, 0.2);
}

.wn-shape--2 {
  width: 80px;
  height: 80px;
  bottom: -20px;
  left: 20%;
  background: rgba(14, 165, 233, 0.15);
}

.wn-shape--3 {
  width: 50px;
  height: 50px;
  top: 20px;
  left: 10%;
  background: rgba(236, 72, 153, 0.12);
}

.wn-card__header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
}

.wn-card__mascot {
  width: 64px;
  height: auto;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  animation: mascot-wave 2s ease-in-out infinite;
}

@keyframes mascot-wave {
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
}

.wn-card__badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 20px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #1e293b;
  font-family: 'Baloo 2', cursive;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.wn-card__title {
  font-family: 'Fredoka One', cursive;
  font-size: 1.3rem;
  font-weight: 400;
  color: #fff;
  margin: 0;
}

.wn-card__close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.wn-card__close:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* ===== FEATURES LIST ===== */
.wn-card__body {
  padding: 16px 24px;
  max-height: 45vh;
  overflow-y: auto;
}

.wn-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  opacity: 0;
  animation: item-slide 0.35s ease forwards;
}

.wn-item:last-child {
  border-bottom: none;
}

@keyframes item-slide {
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
}

.wn-item__emoji {
  font-size: 1.6rem;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 12px;
}

.wn-item__info {
  flex: 1;
  min-width: 0;
}

.wn-item__title {
  font-family: 'Baloo 2', cursive;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
}

.wn-item__desc {
  font-family: 'Baloo 2', cursive;
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* ===== FOOTER ===== */
.wn-card__footer {
  padding: 12px 24px 20px;
}

.wn-card__btn {
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #1e293b;
  font-family: 'Baloo 2', cursive;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(251, 191, 36, 0.25);
}

.wn-card__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(251, 191, 36, 0.4);
}

/* ===== MODAL TRANSITION ===== */
.modal-pop-enter-active {
  transition: opacity 0.3s ease;
}
.modal-pop-enter {
  opacity: 0;
}

/* ===== MOBILE ===== */
@media (max-width: 599px) {
  .splash-mascot__img {
    width: 130px;
  }

  .splash-headline__big {
    font-size: 3.5rem;
  }

  .splash-headline__line1,
  .splash-headline__line3 {
    font-size: 2.2rem;
  }

  .splash-headline {
    min-height: 140px;
  }

  .speech-bubble {
    font-size: 0.72rem;
    padding: 7px 14px;
    right: -140px;
    top: -5px;
  }

  .splash-cta {
    padding: 12px 28px;
    font-size: 0.9rem;
  }

  .wn-card {
    max-width: 100%;
    border-radius: 18px;
  }

  .wn-card__header {
    padding: 22px 18px 16px;
  }

  .wn-card__mascot {
    width: 50px;
  }

  .wn-card__title {
    font-size: 1.1rem;
  }

  .wn-card__body {
    padding: 12px 18px;
  }
}
</style>
